import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { ButtonType, CircleButtonSize } from 'components/buttons/button-types';
import { CircleActionButton } from 'components/buttons/circle-action-button/circle-action-button';
import { AppContext } from 'core/context/app.context';
import { AmountToPayStore } from '../amount-to-pay.store';
import styles from '../amount-to-pay.module.scss';

type AmountInputStyle = {
  containerClasses?: string;
  buttonType: ButtonType;
  inputClass: string;
  alignment?: 'center' | null;
};

type Props = {
  id: string;
  amountToPayStore: AmountToPayStore;
  allowEdit: boolean;
  styleConfig: AmountInputStyle;
};

export const AmountInput = observer<Props>(({ id, amountToPayStore, allowEdit, styleConfig }) => {
  const { currency } = useContext(AppContext);
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const inputLengthRef = useRef<HTMLInputElement>(null);
  const [width, setWidth] = useState(100);
  const inputAlignment = styleConfig?.alignment === 'center' ? 'justify-content-center' : '';

  const onChange = (value: string) => {
    amountToPayStore.changeEditedValue(value);
  };

  useEffect(() => {
    amountToPayStore.isEditingValue ? inputRef.current?.focus() : inputRef.current?.blur();
  }, [amountToPayStore.isEditingValue]);

  useEffect(() => {
    if (!inputLengthRef.current?.offsetWidth) {
      return;
    }

    const minWidth = 20;
    const widthOffset = 10;
    const inputWidth = Math.round(inputLengthRef.current.offsetWidth) + widthOffset;

    setWidth(inputWidth < minWidth ? minWidth : inputWidth);
  }, [
    inputLengthRef.current?.offsetWidth,
    amountToPayStore.formattedValue,
    amountToPayStore.editedValue,
    amountToPayStore.isEditingValue,
    currency,
  ]);

  return (
    <h3 className={styleConfig.containerClasses}>
      <span className={`${styles.textInputRetainer}`} ref={inputLengthRef}>
        {amountToPayStore.editedValue ?? amountToPayStore.formattedValue?.replace(currency.symbol, '')}
      </span>
      <div className={`d-flex align-items-center overflow-hidden gap-1 ${inputAlignment}`}>
        <div>{currency.symbol}</div>
        <div className="overflow-hidden">
          <input
            ref={inputRef}
            id={id}
            type="text"
            name="amountToPay"
            style={{ width: width }}
            onChange={(e) => onChange(e.target.value)}
            className={styleConfig.inputClass}
            disabled={!amountToPayStore.isEditingValue}
            value={amountToPayStore.editedValue ?? amountToPayStore?.formattedValue?.replace(currency.symbol, '')}
          />
        </div>
        {allowEdit && (
          <CircleActionButton
            meta={{
              type: styleConfig.buttonType,
              size: CircleButtonSize.Small,
              title: t('buttons.edit'),
              name: 'download',
              iconName: amountToPayStore.isEditingValue ? 'icon-close' : 'icon-pencil',
              areaName: 'Self Service View Balance',
              actionName: 'edit',
              componentName: 'amount-to-pay-card',
            }}
            onClick={() => {
              amountToPayStore.changeIsEditingValue(!amountToPayStore.isEditingValue);
            }}
            analyticsComponentName="editButton"
          />
        )}
      </div>
    </h3>
  );
});